import { ContentService } from '@blk/cwp-ng-framework';
import { InjectConfig, TaxConfigKey } from '@ddt-web/config-layer';
import * as isNil from 'lodash/isNil';

import { CellUtil } from '../../utils/cell-util';
import { CellData } from '../cell-data';
import { ColumnConfig } from '../column-config';
import { TableData } from '../table-data';
import { TableDataTransform } from './table-data-transform';

/**
 * EstimatedCapitalGainsStatusesTransform is used to add the estimated capital gains statuses and their respective display orders (if provided) 
 * to each CellData[] in the provided TableData object if the following conditions are met:
 *
 * "Status: Pending capital gains estimates":
 * 		Estimated cap gains file (loaded by USWA) does not contain this ticker.
 * 		Check: asOfDate.rawValue or totalCapGains.rawValue is undefined / null.
 * "Status: The fund is not expected to distribute capital gains"
 * 		USWA manually entered ZERO for total capital gains field in the estimated cap gains excel spreadsheet.
 * 		Check: totalCapGains.rawValue is 0.
 * "Status: The fund has already distributed capital gains for the year"
 * 		"Sell by day" equals a day that has passed.
 * 		Check: daysLeft.rawValue <= 0.
 *
 * The transformed TableData object is returned.
 *
 * If none of the conditions above are not met, the provided TableData object is returned.
 */
export class EstimatedCapitalGainsStatusesTransform implements TableDataTransform {

	@InjectConfig(TaxConfigKey.ESTIMATED_CAPITAL_GAIN_STATUSES_ORDER) private readonly capGainStatusOrder: Array<{ displayOrder: number; dcrKey: string }>;

	public transform(data: TableData, contentSvc?: ContentService): TableData {
		const forcedPendingFundsDcrValue = contentSvc.get('FORCED_PENDING_FUNDS', null);
		const forcedPendingFundsList = forcedPendingFundsDcrValue ? forcedPendingFundsDcrValue.split(',') : [];

		if (data && !isNil(data.cells)) {
			data.cells.forEach((cellArray, index) => {
				const asOfDate = CellUtil.getById(cellArray, 'asOfDate')['rawValue'];
				const totalCapGains = CellUtil.getById(cellArray, 'totalCapGains')['rawValue'];
				const daysLeft = CellUtil.getById(cellArray, 'daysLeft')['rawValue'];
				const ticker = CellUtil.getById(cellArray, 'ticker')['rawValue'];

				let key = null;

				if (isNil(asOfDate) || isNil(totalCapGains) || forcedPendingFundsList.includes(ticker as string)) {
					key = 'CAP_GAINS_STATUS_PENDING';
				} else if (totalCapGains === 0) {
					key = 'CAP_GAINS_STATUS_NOT_EXPECTED';
				} else if (daysLeft <= 0) {
					key = 'CAP_GAINS_STATUS_PASSED';
				}

				if (!isNil(key)) {
					// This is used for sorting the estimates table to show rows with values first and then show status rows
					CellUtil.getById(cellArray, 'daysLeft').rawValue = null;
					const statusCellData = CellData.create({
						rawValue: key,
						formattedValue: contentSvc.get(key, key),
						config: ColumnConfig.create({
							id: 'capGainsStatus',
							label: null,
							infoBubble: null,
							extractor: null,
							labelFormatter: null,
							formatters: null,
							visible: null
						}),
						attribute: {}
					});

					const displayOrder = this.capGainStatusOrder?.find(elem => elem?.['dcrKey'] === key)?.displayOrder;
					const statusDisplayOrderCellData = CellData.create({
						rawValue: displayOrder,
						formattedValue: displayOrder as any,
						config: ColumnConfig.create({
							id: 'displayOrder',
							label: null,
							infoBubble: null,
							extractor: null,
							labelFormatter: null,
							formatters: null,
							visible: null
						}),
						attribute: {}
					});

					cellArray.push(statusDisplayOrderCellData);
					cellArray.push(statusCellData);
					data.cells[index] = cellArray;
				}
			});
		}

		return data;
	}
}
